/**
 * Canonical Action lifecycle. Mirrors the backend `Action::STATUSES`
 * constant (`marketpryce-rewards/app/models/action.rb`). The mobile
 * client and backend MUST agree on these values byte-for-byte; never
 * write a status string by hand — reference `ACTION_STATUS.*`.
 *
 * - `Clicked`: user tapped the deal CTA, server recorded the click.
 *   No conversion signal yet.
 * - `Pending`: server received initial conversion signal (e.g. PerformCB
 *   postback) but the Earning row hasn't been created. Brand-side work
 *   in flight.
 * - `Verified`: brand confirmed the conversion. Earning row exists,
 *   payout is queued.
 * - `Paid`: balance has the cash. Terminal success.
 * - `Rejected`: brand explicitly rejected the conversion. Terminal
 *   failure — user gets nothing for this Action.
 * - `Expired`: too much time passed without a verification signal.
 *   Terminal failure.
 */
export const ACTION_STATUS = {
  Clicked: 'clicked',
  Pending: 'pending',
  Verified: 'verified',
  Paid: 'paid',
  Rejected: 'rejected',
  Expired: 'expired',
} as const;
export type ActionStatus = (typeof ACTION_STATUS)[keyof typeof ACTION_STATUS];

/**
 * Statuses where the user is still mid-flow — drives Home's "Active
 * deals" stat and any "in progress" UI treatment.
 */
export const UNRESOLVED_STATUSES: readonly ActionStatus[] = [
  ACTION_STATUS.Clicked,
  ACTION_STATUS.Pending,
];

/**
 * Statuses where the deal ended badly. The DealStepper renders the
 * matching step alone (replaces the stepper) since there's no path
 * forward.
 */
export const TERMINAL_FAILURE_STATUSES: readonly ActionStatus[] = [
  ACTION_STATUS.Rejected,
  ACTION_STATUS.Expired,
];

/**
 * Statuses where the deal ended well — money or its equivalent
 * actually moved.
 */
export const TERMINAL_SUCCESS_STATUSES: readonly ActionStatus[] = [
  ACTION_STATUS.Paid,
];

/**
 * Webhook-driven events that can complete or confirm a deal step. Mirrors
 * the backend `WebhookEvents` canonicals. The client never branches on a
 * specific value (it only checks presence of the step field), so the type
 * stays open: a new canonical on the server must not break deserializing.
 */
export const WEBHOOK_EVENT = {
  PerformcbConversion: 'performcb_conversion',
  PerformcbMainConversion: 'performcb_main_conversion',
  PerformcbRegistration: 'performcb_registration',
  PerformcbInvestAccountStarted: 'performcb_invest_account_started',
  PerformcbInvestAccountCreated: 'performcb_invest_account_created',
  PerformcbFirstDeposit: 'performcb_first_deposit',
  PerformcbFetchSignedUp: 'performcb_fetch_signed_up',
} as const;
export type WebhookEventName = (typeof WEBHOOK_EVENT)[keyof typeof WEBHOOK_EVENT] | (string & {});
